import { useEffect, useState } from "react";
import axios from "axios";
import LecturerSidebar from "../components/LecturerSidebar";
import "../styles/LecturerProfile.css";
import { User, Mail, IdCard, Lock } from "lucide-react";

export default function LecturerProfile() {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null); // { type: 'success' | 'error', text: string }

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/lecturer/profile');
        setProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({
        type: "error",
        text: "Uzupełnij wszystkie pola.",
      });
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage({
        type: "error",
        text: "Nowe hasło powinno mieć co najmniej 8 znaków.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        type: "error",
        text: "Hasła w polach „Nowe hasło” i „Powtórz nowe hasło” muszą być takie same.",
      });
      return;
    }

    try {
      setPasswordLoading(true);

      const res = await axios.post('/lecturer/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setPasswordMessage({
        type: "success",
        text: res.data.message || "Hasło zostało pomyślnie zmienione.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error changing password:", err);
      const errorMessage = err.response?.data?.message || 
        "Nie udało się zmienić hasła. Sprawdź aktualne hasło lub spróbuj ponownie.";
      setPasswordMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="lecture-dashboard-panel">
      <LecturerSidebar />

      <div className="main-content">
        <div className="lecturer-page-inner">
          {/* HEADER */}
          <header className="lecturer-header">
            <div>
              <h1 className="lecturer-header-title">Mój profil</h1>
              <p className="lecturer-header-subtitle">
                Podgląd danych konta oraz zmiana hasła.
              </p>
            </div>
          </header>

          <div className="lecturer-profile-grid">
            {/* LEWA KARTA – DANE PROFILU */}
            <section className="lecturer-profile-card">
              <div className="lecturer-profile-card-header">
                <div className="lecturer-profile-icon">
                  <User size={20} />
                </div>
                <div>
                  <h2>Dane konta</h2>
                  <p>Podstawowe informacje o Twoim profilu.</p>
                </div>
              </div>

              {loadingProfile ? (
                <p>Ładowanie profilu…</p>
              ) : !profile ? (
                <p>Nie udało się załadować danych profilu.</p>
              ) : (
                <dl className="lecturer-profile-data">
                  <div className="lecturer-profile-row">
                    <dt>Imię i nazwisko</dt>
                    <dd>
                      {profile.first_name || profile.last_name
                        ? `${profile.first_name || ""} ${
                            profile.last_name || ""
                          }`.trim()
                        : "—"}
                    </dd>
                  </div>

                  <div className="lecturer-profile-row">
                    <dt>E-mail</dt>
                    <dd className="lecturer-profile-with-icon">
                      <Mail size={16} />
                      <span>{profile.email || "—"}</span>
                    </dd>
                  </div>

                  <div className="lecturer-profile-row">
                    <dt>Login / identyfikator</dt>
                    <dd className="lecturer-profile-with-icon">
                      <IdCard size={16} />
                      <span>{profile.username || profile.login || "—"}</span>
                    </dd>
                  </div>

                  <div className="lecturer-profile-row">
                    <dt>Telefon</dt>
                    <dd>{profile.phone || "—"}</dd>
                  </div>

                  <div className="lecturer-profile-row">
                    <dt>Rola</dt>
                    <dd>{profile.role || "Wykładowca"}</dd>
                  </div>
                </dl>
              )}
            </section>

            {/* PRAWA KARTA – ZMIANA HASŁA */}
            <section className="lecturer-profile-card">
              <div className="lecturer-profile-card-header">
                <div className="lecturer-profile-icon locked">
                  <Lock size={20} />
                </div>
                <div>
                  <h2>Zmiana hasła</h2>
                  <p>
                    Ustaw silne, unikalne hasło – minimum 8 znaków,
                    najlepiej z cyframi i znakami specjalnymi.
                  </p>
                </div>
              </div>

              <form
                className="lecturer-password-form"
                onSubmit={handlePasswordSubmit}
              >
                <div className="lecturer-input-group">
                  <label htmlFor="currentPassword">Aktualne hasło</label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>

                <div className="lecturer-input-group">
                  <label htmlFor="newPassword">Nowe hasło</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="lecturer-input-group">
                  <label htmlFor="confirmPassword">Powtórz nowe hasło</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  className="lecturer-password-btn"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? "Zapisywanie…" : "Zmień hasło"}
                </button>

                {passwordMessage && (
                  <p
                    className={`profile-message profile-message-${passwordMessage.type}`}
                  >
                    {passwordMessage.text}
                  </p>
                )}
              </form>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
